import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  checkAvailability, createReservation, fetchRooms, fetchGuests,
  fetchRoomTypes, createGuest, findCachedConflicts,
} from '../../services/api.js';
import { useOfflineInvalidation } from '../../components/ui.jsx';
import { useOnlineStatus } from '../../hooks/useOnlineStatus.js';

// ─── Constants ────────────────────────────────────────────────────────────────
const SOURCES = [
  { id: 'direct',      label: 'Direct',      icon: '🏨' },
  { id: 'walk_in',     label: 'Walk-in',     icon: '🚶' },
  { id: 'booking_com', label: 'Booking.com', icon: '🌐' },
  { id: 'expedia',     label: 'Expedia',     icon: '✈️' },
  { id: 'airbnb',      label: 'Airbnb',      icon: '🏠' },
  { id: 'orinest_booking', label: 'OrinestBooking',  icon: '🏠'},
  { id: 'phone',       label: 'Phone',       icon: '📞' },
  { id: 'agent',       label: 'Agent',       icon: '🤝' },
  { id: 'other',       label: 'Other',       icon: '📋' },
];
const MEALS = [
  { id: 'room_only',    label: 'Room Only',      short: 'RO', icon: '🛏️' },
  { id: 'bb',           label: 'Bed & Breakfast', short: 'BB', icon: '☕' },
  { id: 'hb',           label: 'Half Board',      short: 'HB', icon: '🍽️' },
  { id: 'fb',           label: 'Full Board',       short: 'FB', icon: '🥘' },
  { id: 'all_inclusive', label: 'All Inclusive',   short: 'AI', icon: '⭐' },
];
// Purpose of Visit — required on the District Guest Report. Defaults to
// "Tourism" so staff are never blocked at booking time; can be changed here
// or corrected later directly in the exported Excel.
const PURPOSES = ['Tourism', 'Business', 'Conference/Meeting', 'Transit', 'Family Visit', 'Other'];

// ─── Extra Person Pricing ──────────────────────────────────────────────────────
// Mirrors the backend OccupancyTariffPricingEngine. Used ONLY for the offline
// fallback (when live availability can't be reached) so the summary still
// shows the right tariff while disconnected. Online, the resolved rate +
// tariff label come straight from the server — this is never used to
// override a server-provided value, only to fill the gap when offline.
function resolveTariff(rt, totalGuests) {
  const maxOccupancy = rt.maxOccupancy || Infinity;
  const standardRate  = Number(rt.baseRate || rt.ratePerNight || 0);
  if (totalGuests <= maxOccupancy) {
    return { rate: standardRate, tariffType: 'standard', tariffLabel: 'Standard Rate' };
  }
  const extra = Number(rt.extraPersonRate || 0);
  if (extra > 0) {
    return {
      rate: extra, tariffType: 'extra_person',
      tariffLabel: `Extra Person Rate (${totalGuests} guests, standard max ${rt.maxOccupancy})`,
    };
  }
  return {
    rate: standardRate, tariffType: 'standard',
    tariffLabel: `Standard Rate (extra-person tariff not configured for ${totalGuests} guests)`,
  };
}

// ─── Micro helpers ────────────────────────────────────────────────────────────
const inp = (extra = {}) => ({
  border: `1.5px solid ${T.border}`, borderRadius: 9, padding: '9px 12px',
  fontSize: 12, fontFamily: FONTS.body, color: T.navy, outline: 'none',
  boxSizing: 'border-box', background: T.white, width: '100%', ...extra,
});
const LBL = {
  fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: 0.8,
  textTransform: 'uppercase', display: 'block', marginBottom: 4,
};
function F({ label, children, required }) {
  return (
    <div>
      <label style={LBL}>
        {label}{required && <span style={{ color: T.danger }}> *</span>}
      </label>
      {children}
    </div>
  );
}
function FI({ value, onChange, type = 'text', placeholder, min, max }) {
  const [focused, setF] = useState(false);
  return (
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} min={min} max={max}
      style={{ ...inp(), borderColor: focused ? T.teal : T.border, transition: 'border-color .15s' }}
      onFocus={() => setF(true)} onBlur={() => setF(false)}
    />
  );
}
function Stepper({ value, onChange, min = 0, max = 20 }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      border: `1.5px solid ${T.border}`, borderRadius: 9,
      overflow: 'hidden', background: T.white, height: 38,
    }}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
        style={{
          width: 36, height: '100%', background: 'none', border: 'none',
          cursor: value <= min ? 'default' : 'pointer',
          fontSize: 18, color: value <= min ? T.border : T.muted,
        }}>−</button>
      <span style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.navy }}>
        {value}
      </span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))}
        style={{
          width: 36, height: '100%', background: 'none', border: 'none',
          cursor: value >= max ? 'default' : 'pointer',
          fontSize: 18, color: value >= max ? T.border : T.muted,
        }}>+</button>
    </div>
  );
}

// ─── Section card wrapper ─────────────────────────────────────────────────────
function Section({ step, title, icon, extra, children }) {
  return (
    <div style={{
      background: T.white, border: `1px solid ${T.border}`,
      borderRadius: 14, padding: '16px 18px', boxShadow: SHADOW.sm,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16 }}>
        <div style={{
          width: 26, height: 26, borderRadius: '50%', background: T.navy, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: T.gold }}>{step}</span>
        </div>
        {icon && <span style={{ fontSize: 15 }}>{icon}</span>}
        <p style={{ fontSize: 13, fontWeight: 700, color: T.navy, fontFamily: FONTS.body }}>{title}</p>
        {extra && <div style={{ marginLeft: 'auto' }}>{extra}</div>}
      </div>
      {children}
    </div>
  );
}

// ─── Room type card ───────────────────────────────────────────────────────────
function RoomCard({ rt, selected, onSelect, cur }) {
  const [hov, setHov] = useState(false);
  const isSel     = selected?.roomTypeId === rt.roomTypeId;
  const rate      = Number(rt.baseRate || rt.ratePerNight || 0);
  const isUnknown = rt.availableCount === null || rt.availableCount === undefined;
  const sold      = !isUnknown && rt.availableCount === 0;

  return (
    <div
      onClick={() => !sold && onSelect(rt)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        border: `2px solid ${isSel ? T.teal : hov && !sold ? `${T.teal}40` : T.border}`,
        borderRadius: 12, padding: '12px 14px',
        cursor: sold ? 'not-allowed' : 'pointer',
        background: isSel ? T.tealPale : sold ? T.surface : T.white,
        opacity: sold ? 0.55 : 1,
        boxShadow: isSel ? `0 0 0 3px ${T.teal}20` : hov && !sold ? SHADOW.md : SHADOW.sm,
        transition: 'all .18s', position: 'relative',
      }}>

      {/* Selected check */}
      {isSel && (
        <div style={{
          position: 'absolute', top: 10, right: 10,
          width: 18, height: 18, borderRadius: '50%',
          background: T.teal, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 800,
        }}>✓</div>
      )}

      {/* Availability badge */}
      <div style={{ marginBottom: 6 }}>
        <span style={{
          fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
          color: isUnknown ? '#78350F' : rt.availableCount > 0 ? T.success : T.danger,
          background: isUnknown ? '#FEF9C3' : rt.availableCount > 0 ? T.successPale : T.dangerPale,
        }}>
          {isUnknown ? '📱 Offline' : rt.availableCount > 0 ? `${rt.availableCount} free` : 'Sold out'}
        </span>
      </div>

      <p style={{ fontSize: 12, fontWeight: 700, color: isSel ? T.teal : T.navy, lineHeight: 1.35, marginBottom: 2 }}>
        {rt.roomTypeName || rt.name}
      </p>
      {rt.bedConfiguration && (
        <p style={{ fontSize: 10, color: T.muted, marginBottom: 5 }}>{rt.bedConfiguration}</p>
      )}

      {/* Extra-person tariff badge */}
      {rt.appliedTariffType === 'extra_person' && (
        <div style={{ marginBottom: 6 }}>
          <span style={{
            fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
            color: '#78350F', background: '#FEF9C3', border: '1px solid #FDE68A',
          }} title={rt.appliedTariffLabel}>
            👥 Extra person rate
          </span>
        </div>
      )}
      {rt.exceedsStandardCapacity && rt.appliedTariffType !== 'extra_person' && (
        <div style={{ marginBottom: 6 }}>
          <span style={{
            fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
            color: '#7C3AED', background: '#EDE9FE', border: '1px solid #C4B5FD',
          }} title={`Exceeds standard max of ${rt.maxOccupancy}`}>
            ⚠️ Over standard capacity
          </span>
        </div>
      )}

      {/* Amenity chips */}
      {rt.amenities && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 7 }}>
          {rt.amenities.split(',').map(a => a.trim()).filter(Boolean).slice(0, 4).map((a, i) => (
            <span key={i} style={{
              fontSize: 8, background: `${T.teal}12`, color: T.teal,
              padding: '1px 6px', borderRadius: 99, fontWeight: 600,
            }}>{a}</span>
          ))}
        </div>
      )}

      {/* Rate */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '6px 8px', borderRadius: 8,
        background: isSel ? `${T.teal}18` : T.surface,
        border: `1px solid ${isSel ? `${T.teal}35` : T.border}`,
        marginTop: 4,
      }}>
        <span style={{ fontSize: 9, color: T.muted }}>per night</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: isSel ? T.teal : T.gold }}>
          {cur || 'RWF'} {rate.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

// ─── Booking summary panel ────────────────────────────────────────────────────
function Summary({ type, arrival, departure, nights, adults, kids, meal, property, onConfirm, saving, error, name, email }) {
  const rate    = Number(type?.baseRate || type?.ratePerNight || 0);
  const cur     = type?.currency || property?.currency || 'RWF';
  const fmt     = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, minimumFractionDigits: 0 }).format(v);
  const ok      = !!(type && arrival && departure && name && email);
  const mealObj = MEALS.find(m => m.id === meal);
  const fmtD    = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';

  return (
    <div style={{
      background: `linear-gradient(160deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
      borderRadius: 16, overflow: 'hidden', boxShadow: SHADOW.xl,
    }}>
      {/* Header */}
      <div style={{ padding: '16px 18px', borderBottom: `1px solid rgba(255,255,255,.1)` }}>
        <p style={{ fontSize: 9, fontWeight: 800, color: T.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>
          Booking Summary
        </p>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', fontFamily: FONTS.body }}>
          {property?.name}
        </p>
      </div>

      {!type ? (
        <div style={{ padding: '36px 18px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🏨</div>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', lineHeight: 1.5 }}>
            Select dates and a room<br />type to see a summary
          </p>
        </div>
      ) : (
        <div style={{ padding: '14px 18px' }}>

          {/* Room type */}
          <div style={{
            background: 'rgba(255,255,255,.08)', borderRadius: 10,
            padding: '10px 13px', marginBottom: 12,
            border: '1px solid rgba(255,255,255,.12)',
          }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: T.white, marginBottom: 2 }}>
              {type.roomTypeName || type.name}
            </p>
            {type.bedConfiguration && (
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>{type.bedConfiguration}</p>
            )}
          </div>

          {/* Date + stay grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {[
              ['Check-in',  fmtD(arrival)],
              ['Check-out', fmtD(departure)],
              ['Nights',    nights || '—'],
              ['Guests',    `${adults}A${kids > 0 ? ` ${kids}C` : ''}`],
            ].map(([l, v]) => (
              <div key={l}>
                <p style={{ fontSize: 9, color: 'rgba(255,255,255,.4)', letterSpacing: 0.7, textTransform: 'uppercase', marginBottom: 2 }}>{l}</p>
                <p style={{ fontSize: 12, fontWeight: 700, color: T.white }}>{v}</p>
              </div>
            ))}
          </div>

          {/* Meal plan (if not room only) */}
          {mealObj?.id !== 'room_only' && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'rgba(255,255,255,.06)', borderRadius: 8,
              padding: '7px 10px', marginBottom: 10,
              border: '1px solid rgba(255,255,255,.1)',
            }}>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,.65)' }}>
                {mealObj?.icon} {mealObj?.label}
              </span>
              <span style={{ fontSize: 8, color: `${T.gold}CC`, fontWeight: 700 }}>EXTRA</span>
            </div>
          )}

          {/* Extra-person tariff banner */}
          {type.appliedTariffType === 'extra_person' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: `${T.gold}1A`, borderRadius: 8,
              padding: '7px 10px', marginBottom: 10,
              border: `1px solid ${T.gold}40`,
            }}>
              <span style={{ fontSize: 12 }}>👥</span>
              <span style={{ fontSize: 10, color: T.gold, fontWeight: 700, lineHeight: 1.35 }}>
                {type.appliedTariffLabel || 'Extra Person Rate applied'}
              </span>
            </div>
          )}

          {/* Guest chip */}
          {name && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,.07)', borderRadius: 8,
              padding: '7px 10px', border: '1px solid rgba(255,255,255,.1)', marginBottom: 12,
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: `${T.teal}60`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 11, fontWeight: 700, color: T.white, flexShrink: 0,
              }}>
                {(name[0] || '?').toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: T.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</p>
                {email && <p style={{ fontSize: 9, color: 'rgba(255,255,255,.45)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</p>}
              </div>
            </div>
          )}

          {/* Price breakdown */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,.12)', paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,.55)' }}>
                {nights || '—'} night{nights !== 1 ? 's' : ''} × {fmt(rate)}
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,.8)' }}>{fmt(rate * nights)}</span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              paddingTop: 10, marginTop: 4, borderTop: '1px solid rgba(255,255,255,.15)',
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.gold }}>Total</span>
              <span style={{ fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, color: T.gold }}>
                {fmt(rate * nights)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          margin: '0 16px 12px', padding: '8px 12px',
          background: 'rgba(192,57,43,.28)', borderRadius: 8,
          border: '1px solid rgba(192,57,43,.45)',
        }}>
          <p style={{ fontSize: 11, color: '#FCA5A5', lineHeight: 1.4 }}>{error}</p>
        </div>
      )}

      {/* Confirm button */}
      <div style={{ padding: '0 16px 18px' }}>
        <button type="button" onClick={onConfirm} disabled={!ok || saving} style={{
          width: '100%', padding: '14px', borderRadius: 12, border: 'none',
          background: ok && !saving
            ? `linear-gradient(135deg, ${T.gold} 0%, ${T.goldLight} 100%)`
            : 'rgba(255,255,255,.1)',
          color: ok && !saving ? T.navy : 'rgba(255,255,255,.25)',
          fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
          cursor: ok && !saving ? 'pointer' : 'not-allowed',
          fontFamily: FONTS.body, transition: 'all .2s',
          boxShadow: ok && !saving ? `0 6px 22px ${T.gold}45` : 'none',
        }}>
          {saving ? '⏳ Creating reservation…' : ok ? '✓ Confirm Reservation' : 'Fill required fields'}
        </button>
        {!ok && (
          <p style={{ textAlign: 'center', marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,.28)' }}>
            {!type       ? 'Select a room type first'
             : !arrival || !departure ? 'Choose check-in & check-out dates'
             : !name     ? 'Enter guest name'
             : !email    ? 'Enter guest email address'
             : 'Fill required fields'}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Guest search ─────────────────────────────────────────────────────────────
function GuestSearch({ pid, onSelect }) {
  const [q,        setQ]   = useState('');
  const [res,      setRes] = useState([]);
  const [busy,     setBusy]    = useState(false);
  const [done,     setDone]    = useState(false);
  const [offline,  setOff]     = useState(false);
  const [newMode,  setNewMode] = useState(false);
  const [ng,       setNg]      = useState({ firstName: '', lastName: '', email: '', phone: '', nationality: '', idType: 'national_id', idNumber: '' });
  const [creating, setCreating] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.length < 2) { setRes([]); setDone(false); setOff(false); return; }
    let cancelled = false;
    timer.current = setTimeout(async () => {
      setBusy(true); setOff(false);
      try {
        const d = await fetchGuests(pid, q);
        if (cancelled) return;
        const raw = Array.isArray(d) ? d : (d?.content || []);
        const seen = new Set();
        setRes(raw.filter(g => { if (seen.has(g.id)) return false; seen.add(g.id); return true; }));
        setDone(true);
      } catch {
        if (cancelled) return;
        try {
          const cached = await fetchGuests(pid, '', 0);
          if (cancelled) return;
          const all = Array.isArray(cached) ? cached : (cached?.content || []);
          const ql = q.toLowerCase();
          setRes(all.filter(g =>
            (g.name || `${g.firstName || ''} ${g.lastName || ''}`).toLowerCase().includes(ql) ||
            (g.email || '').toLowerCase().includes(ql) || (g.phone || '').includes(q)
          ));
          setOff(true);
        } catch { if (!cancelled) setRes([]); }
        if (!cancelled) setDone(true);
      } finally { if (!cancelled) setBusy(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [q, pid]);

  const handleCreate = async () => {
    if (!ng.firstName || !ng.email) return;
    setCreating(true);
    try {
      const g = await createGuest({ ...ng, name: `${ng.firstName} ${ng.lastName}`.trim(), propertyId: pid, idType: ng.idType, idNumber: ng.idNumber });
      onSelect(g);
    } catch (e) { alert(e.message); }
    finally { setCreating(false); }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Search input */}
      <div style={{ position: 'relative', marginBottom: 6 }}>
        <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.muted, fontSize: 13, pointerEvents: 'none' }}>🔍</span>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search registered guest by name or email…"
          style={{ ...inp(), paddingLeft: 32 }}
        />
        {busy && (
          <div style={{
            position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)',
            width: 14, height: 14, border: `2px solid ${T.border}`, borderTopColor: T.teal,
            borderRadius: '50%', animation: 'spin 1s linear infinite',
          }} />
        )}
      </div>

      {/* Offline notice */}
      {offline && q.length >= 2 && (
        <div style={{
          fontSize: 10, color: '#78350F', background: '#FEF9C3',
          border: '1px solid #FDE68A', borderRadius: 7, padding: '4px 10px', marginBottom: 6,
        }}>
          📱 Offline — showing locally cached guests
        </div>
      )}

      {/* Results dropdown */}
      {q.length >= 2 && !busy && (
        <div style={{
          border: `1px solid ${T.border}`, borderRadius: 11, overflow: 'hidden',
          background: T.white, boxShadow: SHADOW.md, marginBottom: 6,
        }}>
          {res.length === 0 && done ? (
            <div style={{ padding: '10px 14px' }}>
              <p style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>No guests found for "{q}"</p>
              <button onClick={() => { setNewMode(true); setQ(''); }} style={{
                fontSize: 11, fontWeight: 700, color: T.teal,
                background: `${T.teal}10`, border: `1px solid ${T.teal}30`,
                borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontFamily: FONTS.body,
              }}>+ Register new guest</button>
            </div>
          ) : res.map(g => (
            <div key={g.id} onClick={() => { onSelect(g); setQ(''); setRes([]); }}
              style={{
                padding: '9px 13px', cursor: 'pointer',
                display: 'flex', gap: 10, alignItems: 'center',
                borderBottom: `1px solid ${T.border}`,
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.surface}
              onMouseLeave={e => e.currentTarget.style.background = T.white}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', background: `${T.teal}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, color: T.teal, flexShrink: 0,
              }}>
                {(g.name || g.firstName || '?')[0]?.toUpperCase()}
              </div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>
                  {g.name || `${g.firstName || ''} ${g.lastName || ''}`.trim()}
                </p>
                <p style={{ fontSize: 10, color: T.muted }}>
                  {g.email}{g.phone ? ` · ${g.phone}` : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New guest form */}
      {newMode && (
        <div style={{
          background: `${T.teal}06`, border: `1px solid ${T.teal}25`,
          borderRadius: 11, padding: '13px 14px', marginBottom: 8,
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: T.teal, marginBottom: 10 }}>
            👤 Register New Guest
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <F label="First Name" required>
              <input value={ng.firstName} onChange={e => setNg(g => ({ ...g, firstName: e.target.value }))}
                placeholder="First" style={inp()} />
            </F>
            <F label="Last Name">
              <input value={ng.lastName} onChange={e => setNg(g => ({ ...g, lastName: e.target.value }))}
                placeholder="Last" style={inp()} />
            </F>
            <F label="Email" required>
              <input type="email" value={ng.email} onChange={e => setNg(g => ({ ...g, email: e.target.value }))}
                placeholder="email@example.com" style={inp()} />
            </F>
            <F label="Phone">
              <input value={ng.phone} onChange={e => setNg(g => ({ ...g, phone: e.target.value }))}
                placeholder="+250 …" style={inp()} />
            </F>
            <F label="ID Type">
              <select value={ng.idType} onChange={e => setNg(g => ({ ...g, idType: e.target.value }))} style={inp()}>
                <option value="national_id">National ID</option>
                <option value="passport">Passport</option>
                <option value="drivers_license">Driver's License</option>
                <option value="other">Other</option>
              </select>
            </F>
            <F label="ID Number">
              <input value={ng.idNumber} onChange={e => setNg(g => ({ ...g, idNumber: e.target.value }))}
                placeholder="Document number" style={inp()} />
            </F>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setNewMode(false)} style={{
              flex: 1, background: 'none', border: `1px solid ${T.border}`,
              borderRadius: 8, padding: '8px', cursor: 'pointer',
              fontSize: 11, color: T.muted, fontFamily: FONTS.body,
            }}>Cancel</button>
            <button onClick={handleCreate} disabled={creating || !ng.firstName || !ng.email}
              style={{
                flex: 2, background: T.teal, border: 'none', borderRadius: 8,
                padding: '8px', cursor: creating || !ng.firstName || !ng.email ? 'not-allowed' : 'pointer',
                fontSize: 11, fontWeight: 700, color: T.white, fontFamily: FONTS.body,
                opacity: creating || !ng.firstName || !ng.email ? 0.6 : 1,
              }}>
              {creating ? 'Registering…' : '✓ Register & Use'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CreateReservation({ property, onCreated, onCancel }) {
  const pid = property?.id;
  const { online } = useOnlineStatus();

  const [arrival,       setArrival]   = useState('');
  const [departure,     setDeparture] = useState('');
  // Historical/manual entry (Feature: record reservations from previous
  // dates) — off by default, so every existing/normal booking flow behaves
  // exactly as before. When enabled, staff may pick a past check-in date.
  const [backdated,     setBackdated] = useState(false);
  const [backdateReason,setBackdateReason] = useState('');
  const [adults,        setAdults]    = useState(2);
  const [kids,          setKids]      = useState(0);
  const [avail,         setAvail]     = useState([]);
  const [offlineAvail,  setOffAvail]  = useState(false);
  const [selType,       setSelType]   = useState(null);
  const [rooms,         setRooms]     = useState([]);
  const [selRoom,       setSelRoom]   = useState('');
  const [name,          setName]      = useState('');
  const [email,         setEmail]     = useState('');
  const [phone,         setPhone]     = useState('');
  const [nat,           setNat]       = useState('');
  const [guestId,       setGuestId]   = useState(null);
  const [selectedGuest, setSG]        = useState(null);
  const [source,        setSource]    = useState('direct');
  const [meal,          setMeal]      = useState('room_only');
  const [purpose,       setPurpose]   = useState('Tourism');
  const [notes,         setNotes]     = useState('');
  const [loading,       setLoading]   = useState(false);
  const [saving,        setSaving]    = useState(false);
  const [error,         setError]     = useState('');
  const [searched,      setSearched]  = useState(false);
  const timer = useRef(null);



  const cur    = property?.currency || 'RWF';
  const nights = (arrival && departure)
    ? Math.max(0, Math.ceil((new Date(departure) - new Date(arrival)) / 86400000)) : 0;
  const today  = new Date().toISOString().split('T')[0];

  const handleSelectGuest = g => {
    setSG(g); setGuestId(g.id);
    setName(g.name || `${g.firstName || ''} ${g.lastName || ''}`.trim());
    setEmail(g.email || ''); setPhone(g.phone || ''); setNat(g.nationality || '');
  };

  const doSearch = useCallback(async (a, d, ads, ch) => {
    if (!a || !d || new Date(d) <= new Date(a)) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true); setError(''); setAvail([]); setSelType(null);
      setSearched(false); setOffAvail(false);
      try {
        const data = await checkAvailability(pid, a, d, ads, ch);
        setAvail(Array.isArray(data) ? data : []);
        setSearched(true);
      } catch {
        try {
          const rts  = await fetchRoomTypes(pid);
          const list = Array.isArray(rts) ? rts : [];
          const totalGuests = (ads || 0) + (ch || 0);
          setAvail(list.filter(rt => rt.active !== false).map(rt => {
            const tariff = resolveTariff(rt, totalGuests);
            return {
              roomTypeId: rt.id, roomTypeName: rt.name, name: rt.name,
              availableCount: null,
              baseRate: tariff.rate, currency: rt.currency || property?.currency,
              bedConfiguration: rt.bedConfiguration, amenities: rt.amenities,
              maxOccupancy: rt.maxOccupancy, extraPersonRate: rt.extraPersonRate,
              appliedTariffType: tariff.tariffType, appliedTariffLabel: tariff.tariffLabel,
            };
          }));
          setOffAvail(true);
          setSearched(true);
        } catch { setError('Unable to load rooms — no cached data available.'); }
      } finally { setLoading(false); }
    }, 500);
  }, [pid, property?.currency]);

  useOfflineInvalidation('availability', () => {
    if (arrival && departure) doSearch(arrival, departure, adults, kids);
  });
  useOfflineInvalidation('reservations', () => {
    if (arrival && departure) doSearch(arrival, departure, adults, kids);
  });
  useEffect(() => { doSearch(arrival, departure, adults, kids); }, [arrival, departure, adults, kids]);

  useEffect(() => {
    if (!selType || !pid) return;
    fetchRooms(pid)
      .then(d => setRooms((Array.isArray(d) ? d : []).filter(r => r.roomTypeId === selType.roomTypeId && r.status === 'available')))
      .catch(() => setRooms([]));
  }, [selType, pid]);

  const handleConfirm = async () => {
    if (!name.trim())  { setError('Guest name is required'); return; }
    if (!email.trim()) { setError('Guest email is required'); return; }
    if (backdated && !backdateReason.trim()) { setError('Please note a reason for back-dating this reservation'); return; }
    setSaving(true); setError('');
    try {
      if (selRoom || selType?.roomTypeId) {
        const conflicts = await findCachedConflicts({
          propertyId: pid, roomId: selRoom || null,
          roomTypeId: selRoom ? null : selType?.roomTypeId,
          arrival, departure,
        });
        if (conflicts.length > 0) {
          const ref  = conflicts[0];
          const conf = ref.confirmationNumber ? ` (${ref.confirmationNumber})` : '';
          const who  = ref.guestName ? ` for ${ref.guestName}` : '';
          setError(selRoom
            ? `This room is already booked${who}${conf} for those dates.`
            : `No rooms of this type are available for those dates${conf}.`);
          setSaving(false); return;
        }
      }
      const ratePerNight    = Number(selType?.baseRate || selType?.ratePerNight || 0);
      const selectedRoomObj = selRoom ? rooms.find(r => r.id === selRoom) : null;
      const res = await createReservation({
        propertyId: pid, roomTypeId: selType?.roomTypeId, roomId: selRoom || null,
        roomNumber: selectedRoomObj?.roomNumber || null, ratePlanId: null,
        arrivalDate: arrival, departureDate: departure, adults, children: kids,
        mealPlan: meal, source, ratePerNight, currency: selType?.currency || property?.currency,
        purposeOfVisit: purpose,
        roomTypeName: selType?.name || null,
        guestId: guestId || null, guestName: name, guestEmail: email,
        guestPhone: phone, guestNationality: nat, specialRequests: notes,
        status: 'confirmed',
        reservationCategory: 'personal',
        corporateAccountId: null,
        isBackdated: backdated,
        backdateReason: backdated ? backdateReason.trim() : null,
      });
      onCreated(res, !!res?._offline);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fade-up">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .cr-wrap { display:grid; grid-template-columns:1fr 292px; gap:14px; align-items:start; }
        @media(max-width:860px){ .cr-wrap { grid-template-columns:1fr; } }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <button onClick={onCancel} style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 9, padding: '7px 14px', fontSize: 12,
          fontWeight: 700, color: T.slate, cursor: 'pointer', fontFamily: FONTS.body,
        }}>← Back</button>
        <div>
          <h1 style={{ fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, color: T.navy, lineHeight: 1.1 }}>
            New Reservation
          </h1>
          <p style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{property?.name}</p>
        </div>
        {!online && (
          <div style={{
            marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#78350F',
            background: '#FEF9C3', border: '1px solid #FDE68A',
            borderRadius: 99, padding: '4px 12px',
          }}>📱 Offline mode</div>
        )}
      </div>

      <div className="cr-wrap">
        {/* ── LEFT PANEL ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* ① Dates & Party */}
          <Section step="1" title="Dates & Party" icon="📅">
            {/* Date row with nights indicator */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'end', marginBottom: 14 }}>
              <F label="Check-in" required>
                <FI type="date" value={arrival} onChange={setArrival} min={backdated ? undefined : today} />
              </F>

              {/* Nights pill */}
              <div style={{ paddingBottom: 5, textAlign: 'center', minWidth: 66 }}>
                {nights > 0 ? (
                  <div style={{
                    background: T.tealPale, border: `1.5px solid ${T.teal}35`,
                    borderRadius: 99, padding: '5px 10px', display: 'inline-block',
                  }}>
                    <div style={{
                      fontFamily: FONTS.display, fontSize: 20, fontWeight: 700,
                      color: T.teal, lineHeight: 1,
                    }}>{nights}</div>
                    <div style={{
                      fontSize: 8, fontWeight: 700, color: T.teal, letterSpacing: 0.8,
                      textTransform: 'uppercase', opacity: 0.75,
                    }}>night{nights !== 1 ? 's' : ''}</div>
                  </div>
                ) : (
                  <span style={{ color: T.softGrey, fontSize: 20, lineHeight: 1.9 }}>→</span>
                )}
              </div>

              <F label="Check-out" required>
                <FI type="date" value={departure} onChange={setDeparture} min={arrival || today} />
              </F>
            </div>

            {/* Historical/manual entry toggle — for recording a stay that
                already happened (e.g. system was down and staff wrote it on
                paper). Hidden behind an explicit toggle so normal bookings
                are unaffected. */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.slate }}>
                <input
                  type="checkbox"
                  checked={backdated}
                  onChange={e => {
                    const checked = e.target.checked;
                    setBackdated(checked);
                    if (!checked) { setBackdateReason(''); if (arrival && arrival < today) setArrival(''); }
                  }}
                />
                Back-date this reservation (record a past stay/booking)
              </label>
              {backdated && (
                <div style={{ marginTop: 8 }}>
                  <F label="Reason for back-dating">
                    <FI value={backdateReason} onChange={setBackdateReason}
                        placeholder="e.g. system was down on the actual booking date" />
                  </F>
                </div>
              )}
            </div>

            {/* Party row */}
            <div style={{
              display: 'flex', gap: 12, paddingTop: 12,
              borderTop: `1px solid ${T.border}`, alignItems: 'flex-end',
            }}>
              <div style={{ flex: 1 }}>
                <label style={LBL}>Adults</label>
                <Stepper value={adults} onChange={setAdults} min={1} max={30} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={LBL}>Children</label>
                <Stepper value={kids} onChange={setKids} min={0} max={20} />
              </div>
              {nights > 0 && (
                <div style={{
                  flex: 1.4, display: 'flex', alignItems: 'center', gap: 7,
                  background: T.surface, borderRadius: 9, padding: '9px 12px',
                  border: `1px solid ${T.border}`,
                }}>
                  <span style={{ fontSize: 14 }}>👥</span>
                  <span style={{ fontSize: 11, color: T.slate }}>
                    {adults} adult{adults !== 1 ? 's' : ''}
                    {kids > 0 ? ` · ${kids} child${kids !== 1 ? 'ren' : ''}` : ''}
                  </span>
                </div>
              )}
            </div>
          </Section>

          {/* ② Room Type */}
          <Section
            step="2" title="Select Room" icon="🏨"
            extra={
              <div style={{ display: 'flex', gap: 6 }}>
                {!online && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#78350F', background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 99, padding: '2px 8px' }}>
                    📱 Offline
                  </span>
                )}
                {loading && (
                  <div style={{ width: 14, height: 14, border: `2px solid ${T.border}`, borderTopColor: T.teal, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                )}
              </div>
            }>

            {offlineAvail && (
              <div style={{
                fontSize: 10, color: '#78350F', background: '#FEF9C3',
                border: '1px solid #FDE68A', borderRadius: 8,
                padding: '6px 10px', marginBottom: 10,
              }}>
                📱 Showing cached room types — live availability will appear when reconnected.
                Double-booking guard still runs at confirmation.
              </div>
            )}

            {!searched && !loading && (
              <div style={{
                textAlign: 'center', padding: '24px 0', color: T.muted,
                border: `2px dashed ${T.border}`, borderRadius: 10,
              }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>📆</div>
                <p style={{ fontSize: 12 }}>Enter dates above to see available rooms</p>
              </div>
            )}

            {searched && avail.length === 0 && !loading && (
              <div style={{
                textAlign: 'center', padding: '24px 0', color: T.danger,
                border: `2px dashed ${T.dangerPale}`, borderRadius: 10,
              }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>😔</div>
                <p style={{ fontSize: 12 }}>No rooms available for those dates — try different dates</p>
              </div>
            )}

            {avail.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(178px,1fr))', gap: 8 }}>
                {avail.map(rt => (
                  <RoomCard key={rt.roomTypeId || rt.id} rt={rt} selected={selType}
                    onSelect={t => { setSelType(t); setSelRoom(''); }} cur={cur} />
                ))}
              </div>
            )}

            {/* Specific room picker */}
            {selType && rooms.length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                <F label="Specific room (optional)">
                  <select value={selRoom} onChange={e => setSelRoom(e.target.value)}
                    style={{ ...inp(), cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7A8D' fill='none' stroke-width='1.5'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', backgroundSize: 12, paddingRight: 30 }}>
                    <option value="">Auto-assign best available</option>
                    {rooms.map(r => (
                      <option key={r.id} value={r.id}>
                        Room {r.roomNumber}{r.floor ? ` · Floor ${r.floor}` : ''}
                      </option>
                    ))}
                  </select>
                </F>
              </div>
            )}
          </Section>

          {/* ③ Guest */}
          <Section
            step="3" title="Guest Information" icon="👤"
            extra={selectedGuest && (
              <button
                onClick={() => { setSG(null); setGuestId(null); setName(''); setEmail(''); setPhone(''); setNat(''); }}
                style={{
                  fontSize: 10, color: T.muted, background: 'none',
                  border: `1px solid ${T.border}`, borderRadius: 7,
                  padding: '3px 10px', cursor: 'pointer', fontFamily: FONTS.body,
                }}>Change guest</button>
            )}>

            {selectedGuest ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 13px', background: T.tealPale,
                border: `1px solid ${T.teal}30`, borderRadius: 10, marginBottom: 12,
              }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', background: T.teal,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: T.white, flexShrink: 0,
                }}>
                  {(name[0] || '?').toUpperCase()}
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>{name}</p>
                  <p style={{ fontSize: 10, color: T.muted }}>{email}{phone ? ` · ${phone}` : ''}</p>
                </div>
              </div>
            ) : (
              <GuestSearch pid={pid} onSelect={handleSelectGuest} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <F label="Full name" required><FI value={name}  onChange={setName}  placeholder="Guest name" /></F>
              <F label="Email"     required><FI value={email} onChange={setEmail} type="email" placeholder="email@…" /></F>
              <F label="Phone">            <FI value={phone} onChange={setPhone} type="tel"   placeholder="+250…" /></F>
              <F label="Nationality">      <FI value={nat}   onChange={setNat}   placeholder="e.g. RW, US" /></F>
            </div>
          </Section>

          {/* ④ Booking Details */}
          <Section step="4" title="Booking Details" icon="⚙️">
            {/* Source */}
            <div style={{ marginBottom: 14 }}>
              <label style={LBL}>Booking source</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {SOURCES.map(s => (
                  <button key={s.id} type="button" onClick={() => setSource(s.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 12px', borderRadius: 99,
                    border: `1.5px solid ${source === s.id ? T.navy : T.border}`,
                    background: source === s.id ? T.navy : T.white,
                    color: source === s.id ? T.white : T.slate,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    fontFamily: FONTS.body, transition: 'all .15s', whiteSpace: 'nowrap',
                  }}>
                    <span>{s.icon}</span>{s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Meal plan */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...LBL, marginBottom: 0 }}>Meal plan</label>
                <span style={{ fontSize: 8, color: T.gold, fontWeight: 700, background: T.goldPale, padding: '1px 7px', borderRadius: 99 }}>
                  EXTRA CHARGE IF NOT RO
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {MEALS.map(m => (
                  <button key={m.id} type="button" onClick={() => setMeal(m.id)} style={{
                    flex: 1, padding: '9px 6px', borderRadius: 10, textAlign: 'center',
                    border: `1.5px solid ${meal === m.id ? T.teal : T.border}`,
                    background: meal === m.id ? T.tealPale : T.white,
                    color: meal === m.id ? T.teal : T.muted,
                    cursor: 'pointer', fontFamily: FONTS.body, transition: 'all .15s',
                  }}>
                    <div style={{ fontSize: 17, marginBottom: 3 }}>{m.icon}</div>
                    <div style={{ fontSize: 9, fontWeight: 800 }}>{m.short}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Purpose of visit — District Guest Report field. Defaults to
                Tourism; staff can pick a different one here or fix it later
                directly in the exported Excel. */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...LBL, marginBottom: 0 }}>Purpose of visit</label>
                <span style={{ fontSize: 8, color: T.muted, fontWeight: 700 }}>
                  FOR DISTRICT REPORT · DEFAULTS TO TOURISM
                </span>
              </div>
              <select value={purpose} onChange={e => setPurpose(e.target.value)} style={{
                width: '100%', padding: '9px 12px', borderRadius: 10,
                border: `1.5px solid ${T.border}`, background: T.white,
                color: T.navy, fontSize: 13, fontFamily: FONTS.body, cursor: 'pointer',
              }}>
                {PURPOSES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* Notes */}
            <F label="Special requests">
              <textarea
                value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Late check-in, extra pillows, dietary needs…"
                rows={2}
                style={{ ...inp(), resize: 'none', lineHeight: 1.55 }}
              />
            </F>
          </Section>

        </div>{/* end left */}

        {/* ── RIGHT: Sticky summary ── */}
        <div style={{ position: 'sticky', top: 16 }}>
          <Summary
            type={selType} arrival={arrival} departure={departure} nights={nights}
            adults={adults} kids={kids} meal={meal} property={property}
            onConfirm={handleConfirm} saving={saving} error={error}
            name={name} email={email}
          />
        </div>
      </div>
    </div>
  );
}